// Task detail modal, shared by the Tasks page and the per-client Tasks tab so a
// task can be opened in place from either without navigating away.
import { useCallback, useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Send } from "lucide-react";
import { formatDateTime, dueColor, relTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import ReasonDialog from "@/components/ReasonDialog";
import { STATUSES, PRIORITIES } from "@/constants/tasks";

export default function TaskDetailDialog({ taskId, onClose, onChange, clients = [], users = [] }) {
  const [task, setTask] = useState(null);
  const [comment, setComment] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const load = useCallback(
    () => api.get(`/tasks/${taskId}`).then(({ data }) => setTask(data)),
    [taskId],
  );
  useEffect(() => { load(); }, [load]);

  const update = async (patch) => {
    await api.patch(`/tasks/${taskId}`, patch);
    load();
    onChange?.();
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/tasks/${taskId}/comments`, { content: comment });
    setComment("");
    load();
  };

  // The backend rejects reasons under 5 chars with a 422, so the reason is
  // collected through ReasonDialog (which enforces the same minimum) rather than
  // a window.prompt that could submit a too-short string.
  const del = async (reason) => {
    try {
      await api.delete(`/tasks/${taskId}`, { headers: { "X-Change-Reason": reason } });
      toast.success("Task deleted");
      setConfirmDelete(false);
      onChange?.();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to delete task"));
    }
  };

  // Resolved against whatever client list the caller has: the full list on the
  // Tasks page, the single current client on the client detail page.
  const clientName = task?.client_id ? clients.find((c) => c.id === task.client_id)?.name : null;

  return (
    <>
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="task-detail-dialog">
        {!task ? (
          <div className="py-12 text-center text-muted-foreground"><Loader2 size={16} className="animate-spin inline mr-2" />Loading…</div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-heading tracking-tight">{task.title}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="col-span-2 space-y-3">
                {task.description && <div className="text-sm text-muted-foreground whitespace-pre-wrap">{task.description}</div>}

                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Comments</div>
                  <div className="space-y-2">
                    {(task.comments || []).map((c) => (
                      <div key={c.id} className="border border-border rounded-md p-2.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium">{c.author_name}</span>
                          <span className="text-muted-foreground font-mono">{relTime(c.created_at)}</span>
                        </div>
                        <div className="text-sm mt-1">{c.content}</div>
                      </div>
                    ))}
                    {(task.comments || []).length === 0 && <div className="text-xs text-muted-foreground">No comments yet.</div>}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Input placeholder="Write a comment…" value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} data-testid="task-comment-input" />
                    <Button size="icon" onClick={addComment} disabled={!comment.trim()} data-testid="task-comment-submit"><Send size={14} /></Button>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Field label="Status">
                  <Select value={task.task_status} onValueChange={(v) => update({ task_status: v })}>
                    <SelectTrigger className="h-8" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Priority">
                  <Select value={task.priority} onValueChange={(v) => update({ priority: v })}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Assignee">
                  <Select value={task.assignee_id || "none"} onValueChange={(v) => update({ assignee_id: v === "none" ? null : v })}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Unassigned —</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Client"><div className="text-sm">{clientName || "—"}</div></Field>
                <Field label="Due">
                  <div className={cn("text-sm font-mono", dueColor(task.due_date))}>{task.due_date ? formatDateTime(task.due_date) : "—"}</div>
                </Field>
                <Field label="Created">
                  <div className="text-xs text-muted-foreground font-mono">{formatDateTime(task.created_at)}</div>
                </Field>
                <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)} className="w-full" data-testid="task-delete">Delete task</Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>

    <ReasonDialog
      open={confirmDelete}
      onOpenChange={setConfirmDelete}
      onConfirm={del}
      title="Delete task"
      description="Deleting is a soft-delete — the task is retained in audit logs. This action requires a reason."
      confirmLabel="Delete task"
    />
    </>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}
